// src/screens/SeasonAnalysisScreen.jsx
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { Container, Row, Col, Form, Card, Alert, Button } from 'react-bootstrap'
import { Chart } from 'react-google-charts'
import { FaChevronUp, FaChevronDown, FaDownload, FaQuestionCircle } from 'react-icons/fa'
import { useGetSquadDataQuery } from '../slices/squadDataApiSlice'
import { useGetShotDataQuery } from '../slices/shotDataApiSlice'
import { useGetGameDataQuery } from '../slices/gameDataApiSlice'
import Loader from '../components/Loader'
import Message from '../components/Message'
import SeasonShotMap from '../components/SeasonShotMap'
import ScreenTabs from '../components/ScreenTabs'

const ANALYSIS_TABS = [
  { label: 'Game Analysis', to: '/ShotAnalysisScreen' },
  { label: 'Player Analysis', to: '/PlayerAnalysisScreen' },
  { label: 'Season Analysis', to: '/SeasonAnalysisScreen' },
]

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const SeasonAnalysisScreen = () => {
  // Fetch data
  const { data: squadData = { entries: [] }, isLoading: playersLoading, error: playersError } = useGetSquadDataQuery()
  const { data: shotDataResp = { entries: [] }, isLoading: shotsLoading, error: shotsError } =
    useGetShotDataQuery({ page: 1, limit: 10000 }, { refetchOnMountOrArgChange: true })
  const { data: gamesDataResp = { entries: [] } } = useGetGameDataQuery({ page: 1, limit: 1000 })

  // State: filters and collapsibles
  const [selectedYear, setSelectedYear] = useState("All")
  const [selectedTeam, setSelectedTeam] = useState('All')
  const [selectedCompetition, setSelectedCompetition] = useState('All')
  const [selectedGame, setSelectedGame] = useState("All")
  const [showOpposition, setShowOpposition] = useState(false)
  const [selectedPlayer, setSelectedPlayer] = useState("All")
  const [openEff, setOpenEff] = useState(true)
  const [openMap, setOpenMap] = useState(true)
  const [openYearEff, setOpenYearEff] = useState(true)
  const hasAutoSelectedTeam = useRef(false)
  const [effChartWrapper, setEffChartWrapper] = useState(null)
  const [yearEffChartWrapper, setYearEffChartWrapper] = useState(null)
  const pageTitleRef = useRef(null)
  const teamYearFiltersRef = useRef(null)
  const gameTypeFilterRef = useRef(null)
  const shotMapCardRef = useRef(null)
  const shotMapEmptyRef = useRef(null)
  const effCardRef = useRef(null)
  const effEmptyRef = useRef(null)
  const yearEffCardRef = useRef(null)
  const yearEffEmptyRef = useRef(null)
  const [showTutorial, setShowTutorial] = useState(false)
  const [tutorialStepIndex, setTutorialStepIndex] = useState(0)
  const [tutorialRect, setTutorialRect] = useState(null)

  const gameById = useMemo(() => {
    const map = {}
    ;(gamesDataResp.entries || []).forEach((g) => {
      if (g?._id) map[g._id] = g
    })
    return map
  }, [gamesDataResp.entries])

  const getYearFromDate = useCallback((value) => {
    if (!value) return null
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return null
    return d.getFullYear()
  }, [])

  const getShotGameYear = useCallback((shot) => {
    const directYear = getYearFromDate(shot?.gameDate)
    if (directYear) return directYear
    const gid = shot?.game || shot?.gameId
    const game = gid ? gameById[gid] : null
    return getYearFromDate(game?.GameDate)
  }, [gameById, getYearFromDate])

  const getShotCompetition = useCallback((shot) => {
    const directCompetition = String(shot?.Competition || shot?.competition || '').trim()
    if (directCompetition) return directCompetition
    const gid = shot?.game || shot?.gameId
    const game = gid ? gameById[gid] : null
    return String(game?.Competition || '').trim()
  }, [gameById])

  const defaultTeam = useMemo(() => {
    const teams = new Set()
    const playerCounts = {}

    ;(squadData.entries || []).forEach((p) => {
      const team = p.team
      if (!team) return
      teams.add(team)
      playerCounts[team] = (playerCounts[team] || 0) + 1
    })

    let best = 'All'
    let bestPlayers = -1

    teams.forEach((team) => {
      const players = playerCounts[team] || 0
      if (
        players > bestPlayers ||
        (players === bestPlayers && (best === 'All' || team.localeCompare(best) < 0))
      ) {
        best = team
        bestPlayers = players
      }
    })

    return best
  }, [squadData.entries])

  useEffect(() => {
    if (hasAutoSelectedTeam.current) return
    if (defaultTeam === 'All') return
    setSelectedTeam(defaultTeam)
    hasAutoSelectedTeam.current = true
  }, [defaultTeam])
  


  // Dropdown options
  const teams = useMemo(() => {
    const set = new Set()
    squadData.entries.forEach(p => p.team && set.add(p.team))
    return ['All', ...Array.from(set)]
  }, [squadData])

  // Shots filtered
  const allShots = shotDataResp.entries

  const availableYears = useMemo(() => {
    const years = new Set()
    ;(gamesDataResp.entries || []).forEach((g) => {
      if (
        selectedTeam !== 'All' &&
        g.HomeTeam !== selectedTeam &&
        g.AwayTeam !== selectedTeam
      ) {
        return
      }
      if (
        selectedCompetition !== 'All' &&
        String(g?.Competition || '').trim() !== selectedCompetition
      ) {
        return
      }
      const year = getYearFromDate(g.GameDate)
      if (year) years.add(year)
    })
    return Array.from(years).sort((a, b) => b - a)
  }, [gamesDataResp.entries, selectedCompetition, selectedTeam, getYearFromDate])

  useEffect(() => {
    if (selectedYear === 'All') return
    if (!availableYears.includes(Number(selectedYear))) {
      setSelectedYear('All')
    }
  }, [availableYears, selectedYear])

  const availableCompetitions = useMemo(() => {
    const competitions = new Set()
    ;(gamesDataResp.entries || []).forEach((g) => {
      if (
        selectedTeam !== 'All' &&
        g.HomeTeam !== selectedTeam &&
        g.AwayTeam !== selectedTeam
      ) {
        return
      }
      if (selectedYear !== 'All') {
        const year = getYearFromDate(g.GameDate)
        if (!year || `${year}` !== selectedYear) return
      }
      const competition = String(g?.Competition || '').trim()
      if (competition) competitions.add(competition)
    })
    return ['All', ...Array.from(competitions).sort((a, b) => a.localeCompare(b))]
  }, [gamesDataResp.entries, selectedTeam, selectedYear, getYearFromDate])

  useEffect(() => {
    if (availableCompetitions.includes(selectedCompetition)) return
    setSelectedCompetition('All')
  }, [availableCompetitions, selectedCompetition])

  const activeTeam = useMemo(() => {
    // ALL GAMES MODE
    if (selectedGame === "All") {
      if (!showOpposition) return selectedTeam

      // opposition across all games
      return "__OPPOSITION_ALL__"
    }

    // SINGLE GAME MODE
    const g = gamesDataResp.entries.find(g => g._id === selectedGame)
    if (!g) return selectedTeam

    if (!showOpposition) return selectedTeam

    return g.HomeTeam === selectedTeam
      ? g.AwayTeam
      : g.HomeTeam

  }, [selectedGame, showOpposition, selectedTeam, gamesDataResp])


  const players = useMemo(() => {
    return [
      "All",
      ...Array.from(new Set(
        allShots
          .filter(s => {
            if (selectedYear !== "All") {
              const y = getShotGameYear(s)
              if (!y || `${y}` !== selectedYear) return false
            }
            if (selectedCompetition !== 'All' && getShotCompetition(s) !== selectedCompetition) {
              return false
            }
            if (activeTeam === "__OPPOSITION_ALL__")
              return s.teamShot !== selectedTeam

            return activeTeam === "All" || s.teamShot === activeTeam
          })
          .map(s => s.playerName)
          .filter(Boolean)
      ))
    ]
  }, [allShots, activeTeam, selectedCompetition, selectedTeam, selectedYear, getShotCompetition, getShotGameYear])

  //console.log(allShots)
  const teamShots = useMemo(() => {
      const filteredByTeam = selectedTeam === 'All' ? allShots : allShots.filter(s => s.teamShot === selectedTeam)
      const filteredByCompetition =
        selectedCompetition === 'All'
          ? filteredByTeam
          : filteredByTeam.filter(s => getShotCompetition(s) === selectedCompetition)
      const filteredByTeamAndYear =
        selectedYear === "All"
          ? filteredByCompetition
          : filteredByCompetition.filter(s => `${getShotGameYear(s)}` === selectedYear)
      return filteredByTeamAndYear
    },
      [allShots, selectedCompetition, selectedTeam, selectedYear, getShotCompetition, getShotGameYear]
    )

  console.log(allShots)

  const filteredShots = useMemo(() => {
    let arr = allShots

    // game

    if (selectedGame !== "All")
      arr = arr.filter(s => (s.game || s.gameId) === selectedGame)

    // team
    if (activeTeam === "__OPPOSITION_ALL__")
      arr = arr.filter(s => s.teamShot !== selectedTeam && (s.homeTeam === selectedTeam || s.awayTeam === selectedTeam))
    else if (activeTeam !== "All")
      arr = arr.filter(s => s.teamShot === activeTeam)

    // player
    if (selectedPlayer !== "All")
      arr = arr.filter(s => s.playerName === selectedPlayer)

    // competition
    if (selectedCompetition !== "All")
      arr = arr.filter(s => getShotCompetition(s) === selectedCompetition)

    // year
    if (selectedYear !== "All")
      arr = arr.filter(s => `${getShotGameYear(s)}` === selectedYear)

    return arr
  }, [allShots, selectedCompetition, selectedGame, activeTeam, selectedPlayer, selectedYear, selectedTeam, getShotCompetition, getShotGameYear])



  // Build game list
  const gameMap = useMemo(() => {
    const m = {}
    gamesDataResp.entries.forEach(g => {
      const label = `${g.HomeTeam} vs ${g.AwayTeam}`
      m[g._id] = label
    })
    return m
  }, [gamesDataResp])

  const gamesOnTeam = useMemo(() => {
    //filter by team
    //console.log("gamesDataResp", gamesDataResp)
    const filtered = gamesDataResp.entries.filter(g =>
      selectedTeam === 'All' || g.HomeTeam === selectedTeam || g.AwayTeam === selectedTeam
    ).filter(g => selectedYear === "All" || `${new Date(g.GameDate).getFullYear()}` === selectedYear)
      .filter(g => selectedCompetition === 'All' || String(g?.Competition || '').trim() === selectedCompetition)

    const opts = filtered.map(g => ({ id: g._id, label: gameMap[g._id] }))
    return [{ id: 'All', label: 'All' }, ...opts]
  }, [gamesDataResp.entries, selectedCompetition, selectedTeam, gameMap, selectedYear])


  // Chart data
  const chartData = useMemo(() => {
    const isOpenPlay = (setPlay) =>
      String(setPlay || '').trim().toLowerCase() === 'open play'

    const categories = [
      { filter: s => s.shotType === '2 Point attempt', label: '2 Point' },
      { filter: s => s.shotType === 'Goal attempt', label: 'Goal' },
      { filter: s => s.shotType === 'Point attempt' && !isOpenPlay(s.setPlay), label: 'Point Dead Ball' },
      { filter: s => s.shotType === 'Point attempt' && isOpenPlay(s.setPlay), label: 'Point Open Play' },
    ]

    const shotsByGame = {}
    teamShots.forEach(s => {
      const gid = s.game || s.gameId
      if (!gid) return
      if (!shotsByGame[gid]) shotsByGame[gid] = []
      shotsByGame[gid].push(s)
    })
    const gamesForChart = gamesDataResp.entries
      .filter(g =>
        (selectedTeam === 'All' || g.HomeTeam === selectedTeam || g.AwayTeam === selectedTeam) &&
        (selectedYear === "All" || `${new Date(g.GameDate).getFullYear()}` === selectedYear) &&
        (selectedCompetition === 'All' || String(g?.Competition || '').trim() === selectedCompetition)
      )
      .sort((a, b) => new Date(a.GameDate) - new Date(b.GameDate))
    const rows = [[
      { label: 'Game', type: 'string' },
      ...categories.flatMap(c => ([
        { label: c.label, type: 'number' },
        { type: 'string', role: 'tooltip', p: { html: true } },
      ])),
    ]]
    const getAxisLabel = (gid) => {
      const game = gamesDataResp.entries.find((g) => g._id === gid)
      if (!game) return gameMap[gid] || gid

      const date = game.GameDate
        ? new Date(game.GameDate).toISOString().split('T')[0]
        : ''
      const opponent =
        selectedTeam === 'All'
          ? (game.AwayTeam || game.HomeTeam || '')
          : game.HomeTeam === selectedTeam
            ? (game.AwayTeam || '')
            : (game.HomeTeam || '')

      return [date, opponent].filter(Boolean).join('\n')
    }
    gamesForChart.forEach((game) => {
        const gid = game._id
        const shots = shotsByGame[gid] || []
        const label = getAxisLabel(gid)
        const row = [ label ]
        categories.forEach(({ filter, label: categoryLabel }) => {
          const arr = shots.filter(filter)
          if (arr.length === 0) {
            row.push(null, null)
            return
          }
          const pts = arr.reduce((sum,s) => {
            if (s.shotOutcome === 'Goal') return sum + 3
            if (s.shotOutcome === '2 Point') return sum + 2
            if (s.shotOutcome === 'Point') return sum + 1
            return sum
          }, 0)
          const xp  = arr.reduce((sum,s) => sum + (s.expectedScore||0), 0)
          const efficiency = xp > 0 ? parseFloat((pts / xp).toFixed(2)) : null
          const dateLabel = game.GameDate
            ? new Date(game.GameDate).toISOString().split('T')[0]
            : 'Unknown date'
          const opponent =
            selectedTeam === 'All'
              ? [game.HomeTeam, game.AwayTeam].filter(Boolean).join(' vs ')
              : game.HomeTeam === selectedTeam
                ? (game.AwayTeam || 'Unknown opponent')
                : (game.HomeTeam || 'Unknown opponent')
          const competition = String(game?.Competition || 'Unspecified')
          const tooltip = efficiency == null
            ? null
            : `<div style="padding:8px 10px; min-width:180px;">
                <div><strong>${escapeHtml(categoryLabel)}</strong></div>
                <div>${escapeHtml(dateLabel)}</div>
                <div>${escapeHtml(opponent)}</div>
                <div>${escapeHtml(competition)}</div>
                <div style="margin-top:6px;">Attempts: ${arr.length}</div>
                <div>Points: ${pts}</div>
                <div>xP: ${xp.toFixed(2)}</div>
                <div>Efficiency: ${efficiency.toFixed(2)}</div>
              </div>`
          row.push(efficiency, tooltip)
        })
        rows.push(row)
      })
    return rows
  }, [teamShots, gamesDataResp, gameMap, selectedCompetition, selectedTeam, selectedYear])

  
  

  
  const yearlyChartData = useMemo(() => {
    const isOpenPlay = (setPlay) =>
      String(setPlay || '').trim().toLowerCase() === 'open play'

    const categories = [
      { filter: s => s.shotType === '2 Point attempt', label: '2 Point' },
      { filter: s => s.shotType === 'Goal attempt', label: 'Goal' },
      { filter: s => s.shotType === 'Point attempt' && !isOpenPlay(s.setPlay), label: 'Point Dead Ball' },
      { filter: s => s.shotType === 'Point attempt' && isOpenPlay(s.setPlay), label: 'Point Open Play' },
    ]

    //group all shots by year/season
    const shotsByYear = {}
    teamShots.forEach(s => {
      const gameYear = new Date(s.gameDate).getFullYear()
      if (!shotsByYear[gameYear]){
        shotsByYear[gameYear] = []
      }
      shotsByYear[gameYear].push(s)
    })

    const rows = [[
      {label: "Year", type: "string"},
      ...categories.map(c => ({label: c.label, type: "number"})),
    ]]

    //axis label is just going to be the year

    Object.keys(shotsByYear)
      .sort((a, b)=> a - b)
      .forEach(year => {
        const shots = shotsByYear[year]
        const label = `${year}`
        const row = [ label ]

        categories.forEach(({filter}) => {
          const arr = shots.filter(filter)
          if (arr.length === 0){
            row.push(null)
            return
          }
          const pts = arr.reduce((sum,s) => {
            if (s.shotOutcome === 'Goal') return sum + 3
            if (s.shotOutcome === '2 Point') return sum + 2
            if (s.shotOutcome === 'Point') return sum + 1
            return sum
          }, 0)

          const xp  = arr.reduce((sum,s) => sum + (s.expectedScore||0), 0)
          row.push(xp > 0 ? parseFloat((pts / xp).toFixed(2)) : null)         
        })
        rows.push(row)
      })
    return rows
  }, [teamShots])

  const hasTutorialData =
    filteredShots.length > 0 || chartData.length > 1 || yearlyChartData.length > 1

  const tutorialSteps = useMemo(() => {
    const baseSteps = [
      {
        title: 'Season Analysis Overview',
        body: 'This screen gives you a season-level view of shot trends in Gaelic football. It combines filters, shot locations, and efficiency charts so you can review how a team performs over time.',
        ref: pageTitleRef,
      },
      {
        title: 'Team and Year Filters',
        body: 'Start here to focus the page on a specific team and season. These two filters control the data shown in the shot map and both efficiency charts.',
        ref: teamYearFiltersRef,
      },
      {
        title: 'Game Type Filter',
        body: 'Use Game Type to narrow the analysis to league, championship, or any other competition type already stored in your game data.',
        ref: gameTypeFilterRef,
      },
    ]

    if (!hasTutorialData) {
      return [
        ...baseSteps,
        {
          title: 'No Season Data Yet',
          body: 'There is no season analysis data available for the current filters yet. For a new user, this is expected until games and shots have been added.',
          ref: shotMapEmptyRef,
        },
        {
          title: 'What Unlocks Next',
          body: 'Once shot data exists, this screen unlocks the season shot map, game-by-game efficiency, and year-by-year trends. Those sections all update from the filters above.',
          ref: effEmptyRef,
        },
      ]
    }

    return [
      ...baseSteps,
      {
        title: 'Shot Map',
        body: 'Use the shot map to review where chances are being taken from. In Gaelic football, this helps you spot central shooting, wide shooting, and the effect of game context filters.',
        ref: shotMapCardRef,
      },
      {
        title: 'Shot Efficiency Over Season',
        body: 'This chart tracks shot efficiency game by game, split by shot type. It is useful for identifying strong or weak stretches over the season.',
        ref: effCardRef,
      },
      {
        title: 'Efficiency By Year',
        body: 'This chart compares shot efficiency across seasons so you can see whether performance is improving, dropping, or holding steady year to year.',
        ref: yearEffCardRef,
      },
    ]
  }, [hasTutorialData])

  useEffect(() => {
    if (!showTutorial) return
    if (tutorialStepIndex > tutorialSteps.length - 1) {
      setTutorialStepIndex(Math.max(0, tutorialSteps.length - 1))
    }
  }, [showTutorial, tutorialStepIndex, tutorialSteps])

  useEffect(() => {
    if (!showTutorial) return
    if (tutorialStepIndex >= 3) setOpenMap(true)
    if (tutorialStepIndex >= 4) setOpenEff(true)
    if (tutorialStepIndex >= 5) setOpenYearEff(true)
  }, [showTutorial, tutorialStepIndex])

  const updateTutorialRect = useCallback(() => {
    if (!showTutorial) {
      setTutorialRect(null)
      return
    }
    const step = tutorialSteps[tutorialStepIndex]
    const node = step?.ref?.current
    if (!node) {
      setTutorialRect(null)
      return
    }
    const rect = node.getBoundingClientRect()
    setTutorialRect({
      top: Math.max(8, rect.top - 8),
      left: Math.max(8, rect.left - 8),
      width: rect.width + 16,
      height: rect.height + 16,
    })
  }, [showTutorial, tutorialStepIndex, tutorialSteps])

  useEffect(() => {
    if (!showTutorial) return undefined
    const step = tutorialSteps[tutorialStepIndex]
    step?.ref?.current?.scrollIntoView({
      behavior: 'smooth',
      block: tutorialStepIndex >= 3 ? 'start' : 'center',
    })
    const refresh = () => updateTutorialRect()
    refresh()
    const timeoutId = window.setTimeout(refresh, 220)
    const secondTimeoutId = window.setTimeout(refresh, 420)
    const intervalId = window.setInterval(refresh, 70)
    const stopIntervalId = window.setTimeout(() => {
      window.clearInterval(intervalId)
    }, 800)
    window.addEventListener('resize', refresh)
    window.addEventListener('scroll', refresh, true)
    return () => {
      window.clearTimeout(timeoutId)
      window.clearTimeout(secondTimeoutId)
      window.clearTimeout(stopIntervalId)
      window.clearInterval(intervalId)
      window.removeEventListener('resize', refresh)
      window.removeEventListener('scroll', refresh, true)
    }
  }, [showTutorial, tutorialStepIndex, tutorialSteps, updateTutorialRect])

  const startTutorial = () => {
    setTutorialStepIndex(0)
    setShowTutorial(true)
  }

  const closeTutorial = () => {
    setShowTutorial(false)
    setTutorialRect(null)
  }

  const nextTutorialStep = () => {
    if (tutorialStepIndex >= tutorialSteps.length - 1) {
      closeTutorial()
      return
    }
    setTutorialStepIndex((prev) => prev + 1)
  }

  const prevTutorialStep = () => {
    setTutorialStepIndex((prev) => Math.max(prev - 1, 0))
  }

  const getTutorialCardStyle = () => {
    if (!tutorialRect) return {}
    const cardWidth = Math.min(360, window.innerWidth - 32)
    const gap = 16
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const placeBelow =
      tutorialRect.top + tutorialRect.height + 220 + gap < viewportHeight
    const top = placeBelow
      ? tutorialRect.top + tutorialRect.height + gap
      : Math.max(16, tutorialRect.top - 220 - gap)
    const preferredLeft = tutorialRect.left
    const maxLeft = Math.max(16, viewportWidth - cardWidth - 16)
    const left = Math.min(Math.max(16, preferredLeft), maxLeft)
    return {
      position: 'fixed',
      top,
      left,
      width: cardWidth,
      zIndex: 1062,
      boxShadow: '0 16px 40px rgba(0, 0, 0, 0.35)',
      transition: 'top 160ms ease, left 160ms ease',
    }
  }

  if (playersLoading || shotsLoading) return <Loader />
  if (playersError) return <Message variant="danger">{playersError.error}</Message>
  if (shotsError) return <Message variant="danger">Failed to load shots</Message>

  const downloadChart = (chartWrapper, filename) => {
    if (!chartWrapper) return
    const uri = chartWrapper.getChart().getImageURI()
    const link = document.createElement('a')
    link.href = uri
    link.download = filename
    link.click()
  }


  return (
    <Container fluid className="py-4 app-section-page">
      <title>Season Analysis | ShotArc</title>
      <ScreenTabs items={ANALYSIS_TABS} />
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <h2 ref={pageTitleRef} className="app-page-title mb-0">Season Analysis</h2>
        <Button
          variant="outline-light"
          size="sm"
          className="d-inline-flex align-items-center gap-2"
          onClick={startTutorial}
        >
          <FaQuestionCircle />
          Help Tour
        </Button>
      </div>

      {/* Team selector */}
      <Form.Group ref={teamYearFiltersRef} as={Row} className="mb-3" controlId="teamSelect">
        <Form.Label column sm={2}>Select Team:</Form.Label>
        <Col sm={4}>
          <Form.Select
            value={selectedTeam}
            onChange={e => {
              const team = e.target.value
              setSelectedTeam(team)

              // reset dependent filters
              setShowOpposition(false)
              setSelectedPlayer("All")
              setSelectedGame("All")
            }}

          >
            {teams.map(t => <option key={t} value={t}>{t}</option>)}
          </Form.Select>
        </Col>

        <Form.Label column sm={2}>Select Year:</Form.Label>
        <Col sm={4}>
          <Form.Select
            value={selectedYear}
            onChange={e=>{setSelectedYear(e.target.value);setSelectedGame("All");setSelectedPlayer("All")}}
          >
            <option value="All">All</option>
            {availableYears.map(year => (
              <option key={year} value={`${year}`}>{year}</option>
            ))}
          </Form.Select>
        </Col>

      </Form.Group>
      <Form.Group ref={gameTypeFilterRef} as={Row} className="mb-3" controlId="competitionSelect">
        <Form.Label column sm={2}>Game Type:</Form.Label>
        <Col sm={4}>
          <Form.Select
            value={selectedCompetition}
            onChange={e => {
              setSelectedCompetition(e.target.value)
              setSelectedGame("All")
              setSelectedPlayer("All")
            }}
          >
            {availableCompetitions.map((competition) => (
              <option key={competition} value={competition}>{competition}</option>
            ))}
          </Form.Select>
        </Col>
      </Form.Group>

      {/* Shot Map */}
      <Card ref={shotMapCardRef} className="border-warning mb-4">
        <Card.Header
          className="bg-warning text-dark d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer' }}
          onClick={() => setOpenMap(o => !o)}
        >
          <strong>Shot Map</strong>
          {openMap ? <FaChevronUp/> : <FaChevronDown/>}
        </Card.Header>
        {openMap && (
          <Card.Body className="bg-dark text-white">
            {/* Filters */}

<Row className="g-3 mb-3 align-items-center">

  {/* Game */}
  <Col xs={12} md={5}>
    <Row className="align-items-center">
      <Col xs={3} className="text-md-end">
        <Form.Label>Game</Form.Label>
      </Col>
      <Col xs={9}>
        <Form.Select
          className="w-100"
          value={selectedGame}
          onChange={e => {
            setSelectedGame(e.target.value)
            setSelectedPlayer("All")
          }}
        >
          {gamesOnTeam.map(g => (
            <option key={g.id} value={g.id}>{g.label}</option>
          ))}
        </Form.Select>
      </Col>
    </Row>
  </Col>

  {/* Switch */}
  <Col xs="auto" className="text-center">
    <Form.Check
      type="switch"
      id="opp-switch"
      checked={showOpposition}
      onChange={e => {
        setShowOpposition(e.target.checked)
        setSelectedPlayer("All")
      }}
      style={{ fontSize: "25px" }}
    />
    <div className="small text-white">
      {showOpposition ? 
        <p><span className="text-secondary">Team</span> / <strong>Opponent</strong></p> :
        <p><strong>Team</strong> / <span className="text-secondary">Opponent</span></p>
      }
    </div>
  </Col>

  
  

  {/* Player */}
  <Col xs={12} md={5}>
    <Row className="align-items-center">
      <Col xs={3} className="text-md-end">
        <Form.Label>Player</Form.Label>
      </Col>
      <Col xs={9}>
        <Form.Select  
          className="w-100"
          value={selectedPlayer}
          onChange={e => setSelectedPlayer(e.target.value)}
        >
          {players.map(p => (
            <option key={p}>{p}</option>
          ))}
        </Form.Select>
      </Col>
    </Row>
  </Col>

</Row>





  
            {/* Map or empty state */}
            {filteredShots.length > 0 ? (
              <SeasonShotMap shotsData={filteredShots} />
            ) : (
              <Alert ref={shotMapEmptyRef} className="app-empty-state mb-0">No shot data for selected player / game combination</Alert>
            )}
          </Card.Body>
        )}
      </Card>

      {/* Efficiency Chart */}
      <Card ref={effCardRef} className="border-warning mb-4">
        <Card.Header
          className="bg-warning text-dark d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer' }}
          onClick={() => setOpenEff(o => !o)}
        >
          <strong>Shot Efficiency over Season</strong>
          {openEff ? <FaChevronUp/> : <FaChevronDown/>}
        </Card.Header>
        {openEff && (
          <Card.Body className="bg-dark text-white">
            {chartData.length > 1 ? (
              <>
                <div className="d-flex justify-content-end mb-2">
                  <Button
                    size="sm"
                    variant="outline-light"
                    onClick={() =>
                      downloadChart(
                        effChartWrapper,
                        `season-efficiency-${String(selectedTeam || 'team').replace(/[^\w-]+/g, '_')}.png`,
                      )
                    }
                    disabled={!effChartWrapper}
                  >
                    <FaDownload className="me-1" />
                    Download Chart
                  </Button>
                </div>
                <Chart
                  chartType="LineChart"
                  width="100%"
                  height="60vh"
                  loader={<div>Loading...</div>}
                  data={chartData}
                  chartEvents={[
                    {
                      eventName: "ready",
                      callback: ({ chartWrapper }) => setEffChartWrapper(chartWrapper),
                    },
                  ]}
                  options={{
                    curveType: 'none',
                    interpolateNulls: false,
                    focusTarget: 'datum',
                    pointSize: 8,
                    lineWidth: 3,
                    backgroundColor: '#d9d9d9',
                    chartArea: { left: 70, right: 220, top: 30, bottom: 80, width: '83%' },
                    colors: ['#28a745', '#6f42c1', '#dc3545', '#0d6efd'],
                    hAxis: {
                      title: 'Game',
                      slantedText: false,
                      textStyle: { fontSize: 12 },
                    },
                    vAxis: {
                      title: 'Shot Efficiency',
                      minValue: 0,
                      viewWindow: { min: 0 },
                    },
                    tooltip: { isHtml: true, trigger: 'focus' },
                    legend: { position: 'right' },
                  }}
                />
              </>
            ) : (
              <Alert ref={effEmptyRef} className="app-empty-state mb-0">No shot data for selected team</Alert>
            )}
          </Card.Body>
        )}
      </Card>


      {/* Year on year efficiency  */}
      <Card ref={yearEffCardRef} className="border-warning mb-4">
        <Card.Header
          className="bg-warning text-dark d-flex justify-content-between align-items-center"
          style={{ cursor: "pointer" }}
          onClick={()=>{setOpenYearEff(!openYearEff)}}
        >
          <strong>Efficiency By Year</strong>   
          {openYearEff ? <FaChevronUp /> : <FaChevronDown />}
        </Card.Header>
        {openYearEff && (
          <Card.Body className="bg-dark text-white">
            {yearlyChartData.length > 1 ? (
                <>
                  <div className="d-flex justify-content-end mb-2">
                    <Button
                      size="sm"
                      variant="outline-light"
                      onClick={() =>
                        downloadChart(
                          yearEffChartWrapper,
                          `year-efficiency-${String(selectedTeam || 'team').replace(/[^\w-]+/g, '_')}.png`,
                        )
                      }
                      disabled={!yearEffChartWrapper}
                    >
                      <FaDownload className="me-1" />
                      Download Chart
                    </Button>
                  </div>
                  <Chart 
                    chartType="LineChart"
                    width="100%"
                    height="60vh"
                    loader={<div>Loading...</div>}
                    data={yearlyChartData}
                    chartEvents={[
                      {
                        eventName: "ready",
                        callback: ({ chartWrapper }) => setYearEffChartWrapper(chartWrapper),
                      },
                    ]}
                    options={{ 
                      curveType: "none",
                      pointSize: 8,
                      lineWidth: 3,
                      backgroundColor: "#d9d9d9",
                      chartArea: { left: 70, right: 220, top: 30, bottom: 80, width: '83%' },
                      colors: ['#28a745', '#6f42c1', '#dc3545', '#0d6efd'],
                      hAxis: {
                        title: "Season",
                        slantedText: false,
                        textStyle: {fontSize: 12}
                      },
                      vAxis: {
                        title: "Shot Efficiency",
                        minValue: 0,
                        viewWindow: {min: 0},
                      },
                      legend: {position: "right"}
                     }}
                  />
                </>
              ) :
              <Alert ref={yearEffEmptyRef} className="app-empty-state mb-0">No Data</Alert>
            }
          </Card.Body>
        )}
      </Card>

      {showTutorial && (
        <>
          {tutorialRect ? (
            <>
              <div
                onClick={closeTutorial}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: tutorialRect.top,
                  background: 'rgba(8, 12, 18, 0.72)',
                  zIndex: 1060,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: 'fixed',
                  top: tutorialRect.top,
                  left: 0,
                  width: tutorialRect.left,
                  height: tutorialRect.height,
                  background: 'rgba(8, 12, 18, 0.72)',
                  zIndex: 1060,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: 'fixed',
                  top: tutorialRect.top,
                  left: tutorialRect.left + tutorialRect.width,
                  width: `calc(100vw - ${tutorialRect.left + tutorialRect.width}px)`,
                  height: tutorialRect.height,
                  background: 'rgba(8, 12, 18, 0.72)',
                  zIndex: 1060,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: 'fixed',
                  top: tutorialRect.top + tutorialRect.height,
                  left: 0,
                  width: '100vw',
                  height: `calc(100vh - ${tutorialRect.top + tutorialRect.height}px)`,
                  background: 'rgba(8, 12, 18, 0.72)',
                  zIndex: 1060,
                }}
              />
              <div
                style={{
                  position: 'fixed',
                  top: tutorialRect.top,
                  left: tutorialRect.left,
                  width: tutorialRect.width,
                  height: tutorialRect.height,
                  border: '2px solid #f59e0b',
                  borderRadius: 12,
                  pointerEvents: 'none',
                  zIndex: 1061,
                  transition: 'top 160ms ease, left 160ms ease, width 160ms ease, height 160ms ease',
                }}
              />
              <Card className="border-warning" style={getTutorialCardStyle()}>
                <Card.Header className="bg-warning text-dark d-flex justify-content-between align-items-center">
                  <strong>
                    {tutorialStepIndex + 1} / {tutorialSteps.length} {tutorialSteps[tutorialStepIndex].title}
                  </strong>
                  <Button
                    variant="link"
                    className="text-dark p-0 text-decoration-none fw-semibold"
                    onClick={closeTutorial}
                  >
                    Close
                  </Button>
                </Card.Header>
                <Card.Body className="bg-dark text-white">
                  <p className="mb-3">{tutorialSteps[tutorialStepIndex].body}</p>
                  <div className="d-flex justify-content-between gap-2">
                    <Button
                      variant="outline-light"
                      size="sm"
                      disabled={tutorialStepIndex === 0}
                      onClick={prevTutorialStep}
                    >
                      Back
                    </Button>
                    <Button
                      variant="warning"
                      size="sm"
                      className="text-dark fw-semibold"
                      onClick={nextTutorialStep}
                    >
                      {tutorialStepIndex === tutorialSteps.length - 1 ? 'Finish' : 'Next'}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </>
          ) : (
            <div
              onClick={closeTutorial}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(8, 12, 18, 0.72)',
                zIndex: 1060,
              }}
            />
          )}
        </>
      )}

    </Container>
  )
}

export default SeasonAnalysisScreen

